import { ActionContext as VuexActionContext, Store } from 'vuex'
import * as service from '@/services/area.service'
import { AreaInfoResponse, Apartment, Address } from '@/types/area-response.types'
import { getAddress, setAddress } from '@/services/local-storage.service'

export const NAME_SPACE = 'area'

const namespace = (fragment: string) => `${NAME_SPACE}/${fragment}`

export enum Property {
  Loading = 'loading',
  Exception = 'exception',
  AreaInfo = 'areaInfo',
  SelectedAddress = 'selectedAddress'
}

enum Setter {
  Loading = 'setLoading',
  Exception = 'setException',
  AreaInfo = 'setAreaInfo',
  SelectedAddress = 'setSelectedAddress'
}

enum Action {
  FetchAreaInfo = 'fetchAreaInfo',
  Init = 'init',
  ClearException = 'clearException'
}

interface State {
  [Property.Loading]: boolean;
  [Property.Exception]: boolean;
  [Property.AreaInfo]: AreaInfoResponse;
  [Property.SelectedAddress]: Address & Apartment;
}

export type ActionContext = VuexActionContext<State, {}>

export const getLoading = (store: Store<{}>): boolean => store.getters[namespace(Property.Loading)]
export const setLoading = (store: Store<{}>, loading: boolean): void => store.commit(namespace(Setter.Loading), loading)
export const getException = (store: Store<{}>): boolean => store.getters[namespace(Property.Exception)]
export const getAreaInfo = (store: Store<{}>): AreaInfoResponse => store.getters[namespace(Property.AreaInfo)]
export const fetchAreaInfo = (store: Store<{}>, payload: Apartment): Promise<void> => store.dispatch(namespace(Action.FetchAreaInfo), payload)
export const getSelectedAddress = (store: Store<{}>): Address & Apartment => store.getters[namespace(Property.SelectedAddress)]
export const setSelectedAddress = (store: Store<{}>, selectedAddress: Address & Apartment): void => store.commit(namespace(Setter.SelectedAddress), selectedAddress)
export const clearException = (store: Store<{}>): Promise<void> => store.dispatch(namespace(Action.ClearException))

export const initAreaStore = async (store: Store<{}>): Promise<void> => {
  const address = getSelectedAddress(store) || getAddress()

  if (!address) {throw Error('no saved address')}

  await fetchAreaInfo(store, address)

  const areaInfo = getAreaInfo(store)

  if (!areaInfo || !areaInfo.AreaTypeName || !areaInfo.AreaStatusName || getException(store)) {throw Error('area info exception')}
}

const areaState = {
  namespaced: true,
  state: {
    [Property.Loading]: false,
    [Property.Exception]: false,
    [Property.AreaInfo]: {} as AreaInfoResponse,
    [Property.SelectedAddress]: null
  },
  getters: {
    [Property.Loading]: (state: State) => state.loading,
    [Property.Exception]: (state: State) => state.exception,
    [Property.AreaInfo]: (state: State) => state.areaInfo,
    [Property.SelectedAddress]: (state: State) => state.selectedAddress
  },
  mutations: {
    [Setter.Loading]: (state: State, loading: boolean) => {
      state.loading = loading
    },
    [Setter.Exception]: (state: State, exception: boolean) => {
      state.exception = exception
    },
    [Setter.AreaInfo]: (
      state: State,
      areaInfo: AreaInfoResponse
    ) => {
      state.areaInfo = areaInfo
    },
    [Setter.SelectedAddress]: (
      state: State,
      selectedAddress: Address & Apartment
    ) => {
      setAddress(selectedAddress)
      state.selectedAddress = selectedAddress
    }
  },
  actions: {
    [Action.FetchAreaInfo]: async ({ commit }: ActionContext, payload: Apartment): Promise<void> => {
      commit(Setter.Exception, false)
      try {
        commit(Setter.Loading, true)
        const areaInfo = await service.getAreaInfo(payload.AddressId)
        commit(Setter.AreaInfo, areaInfo)
      } catch (err) {
        commit(Setter.Exception, true)
      } finally {
        commit(Setter.Loading, false)
      }
    },
    [Action.ClearException]: async ({ commit }: ActionContext): Promise<void> => {
      commit(Setter.Exception, false)
    }
  }
}

export default areaState
